/**
 * Environment configuration.
 *
 * ⚠️ This file exists to fix one specific defect of V1: there, the API address is
 * GUESSED from the browser hostname, with a CloudFront id hard-coded in the source
 * (`fo-meeting/src/services/fetch.ts:3-8`). A new domain and the front silently
 * queries the wrong API.
 *
 * Here the address comes from build configuration, and its absence is a LOUD
 * failure at startup. An explicit blank page on first load beats a front office
 * writing into the wrong database.
 */

export type Config = {
  /** api-v1 root — context, sign-in. Without a trailing slash. */
  apiUrl: string;
  /** apix root — front-office settings, languages. Without a trailing slash. */
  apixUrl: string;
  /**
   * ═══ LE CANAL DU DIRECT, ET POURQUOI IL EST FACULTATIF ═══
   *
   * Les deux adresses ci-dessus sont exigées : sans elles, il n'y a pas de site.
   * Celle-ci ne conditionne qu'un écran, et son absence a un repli honnête — le
   * direct interroge `GET /live` au lieu d'être poussé.
   *
   * ⚠️ L'EXIGER AURAIT CASSÉ TOUS LES DÉPLOIEMENTS EXISTANTS au prochain build,
   * pour une page qui n'existait pas la veille. Un réglage absent qui dégrade
   * proprement vaut mieux qu'un démarrage refusé.
   */
  wsUrl: string | null;
  /** Environment name, for traces and the debug ribbon. */
  environment: "local" | "dev" | "prod";
};

/** Configuration error: it must stop startup, not be swallowed. */
export class MissingConfig extends Error {
  constructor(key: string) {
    super(
      `Missing configuration: ${key}. Set it in a .env file (see .env.example) or ` +
        "in the deployment workflow variables.",
    );
    this.name = "MissingConfig";
  }
}

const ENVIRONMENTS = ["local", "dev", "prod"] as const;

function isEnvironment(value: string): value is Config["environment"] {
  return (ENVIRONMENTS as readonly string[]).includes(value);
}

/**
 * Pure function: takes the variable source and returns the configuration. Kept
 * separate from reading `import.meta.env` so it stays testable — and so that
 * importing it never triggers a read (see `./index.ts`).
 */
export function readConfig(source: Record<string, string | undefined>): Config {
  const apiUrl = source.VITE_API_URL?.trim();
  if (!apiUrl) throw new MissingConfig("VITE_API_URL");

  // ⚠️ TWO APIs, both declared, NEITHER guessed. api-v1 resolves the context and
  // signs people in; apix holds the front-office settings and the languages. V1
  // works out both from the hostname, which is why a preview domain or a client's
  // own domain sends it to the wrong server — the exact fragility this file
  // exists to remove.
  const apixUrl = source.VITE_APIX_URL?.trim();
  if (!apixUrl) throw new MissingConfig("VITE_APIX_URL");

  const raw = source.VITE_ENVIRONMENT?.trim() ?? "local";
  if (!isEnvironment(raw)) {
    throw new Error(
      `VITE_ENVIRONMENT is "${raw}"; expected one of: ${ENVIRONMENTS.join(", ")}.`,
    );
  }

  const wsUrl = source.VITE_WS_URL?.trim();

  return {
    // Trailing slash stripped once and for all: callers build `${apiUrl}/path`,
    // and a double slash breaks some gateways.
    apiUrl: apiUrl.replace(/\/+$/, ""),
    apixUrl: apixUrl.replace(/\/+$/, ""),
    wsUrl: wsUrl ? wsUrl.replace(/\/+$/, "") : null,
    environment: raw,
  };
}
